import React, { useState } from "react";
import Sidebar from "../components/Sidebar";
import { useNavigate } from "react-router-dom";
import DateTimeHeader from "../components/DateTimeHeader";
import {
  HelpCircle,
  MessageCircle,
  Book,
  Video,
  Mail,
  Phone,
  Search,
  ChevronRight,
  FileText,
  ExternalLink,
  Clock,
  CheckCircle,
  AlertCircle,
  Moon,
  Sun,
  RefreshCw,
  Bell,
  Shield,
} from 'lucide-react';
import ProfileModal from "../components/ProfileModal";
import Dropdown from "../components/Dropdown";
import { useTheme } from "../contexts/ThemeContext";
import NavBar from "../components/NavBar";

const HelpSupport: React.FC = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  // const [darkMode, setDarkMode] = useState(false); 
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<
    "getting-started" | "meetings" | "troubleshooting" | "account" | "contact"
  >("getting-started");
  const [notifications] = useState([
    { id: 1, message: "Welcome to Help & Support", unread: false },
  ]);
  const { darkMode, toggleDarkMode } = useTheme();

  const categories = [
    {
      id: "getting-started",
      label: "Getting Started",
      icon: <Book size={20} />,
    },
    { id: "meetings", label: "Meetings & Calls", icon: <Video size={20} /> },
    {
      id: "troubleshooting",
      label: "Troubleshooting",
      icon: <AlertCircle size={20} />,
    },
    {
      id: "account",
      label: "Account & Settings",
      icon: <HelpCircle size={20} />,
    },
    {
      id: "contact",
      label: "Contact Support",
      icon: <MessageCircle size={20} />,
    },
  ];

  const faqData = {
    "getting-started": [
      {
        question: "How do I create my first meeting?",
        answer:
          'Click the "Create Meeting" button on your dashboard or use the Quick Actions section. You can start an instant meeting or schedule one for later.',
      },
      {
        question: "How do I invite participants to a meeting?",
        answer:
          "When creating a meeting, add participant emails in the invite section. They will receive an email with the meeting link and details.",
      },
      {
        question: "What are the system requirements?",
        answer:
          "You need a modern web browser (Chrome, Firefox, Safari, or Edge), a stable internet connection, and optionally a camera and microphone for video calls.",
      },
    ],
    meetings: [
      {
        question: "How do I join a meeting?",
        answer:
          'Click the meeting link provided in your email invitation, or enter the Meeting ID in the "Join Meeting" section on your dashboard.',
      },
      {
        question: "Can I record meetings?",
        answer:
          "Yes, you can enable recording in the meeting security settings. All participants will be notified when recording starts.",
      },
      {
        question: "How do I share my screen?",
        answer:
          "Click the screen share button during a meeting. You can choose to share your entire screen, a specific window, or just a browser tab.",
      },
    ],
    troubleshooting: [
      {
        question: "My camera or microphone is not working",
        answer:
          "Check your browser permissions and ensure the camera/microphone is not being used by another application. Try refreshing the page.",
      },
      {
        question: "Poor video or audio quality",
        answer:
          "Check your internet connection, close unnecessary applications, and ensure you have sufficient bandwidth. Consider switching to audio-only mode.",
      },
      {
        question: "Cannot join meetings",
        answer:
          "Verify the meeting ID is correct, check your internet connection, and ensure your browser is up to date. Try using a different browser.",
      },
    ],
    account: [
      {
        question: "How do I change my profile information?",
        answer:
          "Go to Settings > Profile to update your name, job title, department, and other personal information.",
      },
      {
        question: "How do I enable two-factor authentication?",
        answer:
          "Navigate to Settings > Security and toggle on two-factor authentication. Follow the setup instructions to secure your account.",
      },
      {
        question: "How do I change my password?",
        answer:
          'Go to Settings > Security and click "Change Password". Enter your current password and set a new one.',
      },
    ],
    contact: [
      {
        question: "How can I contact support?",
        answer:
          "Use the contact methods below, or submit a support ticket through our help center. We aim to respond within 24 hours.",
      },
    ],
  };

  const contactMethods = [
    {
      icon: <Mail size={24} />,
      title: "Email Support",
      description: "Get help via email",
      contact: "Support@vela.business",
      availability: "24/7",
    },
    {
      icon: <MessageCircle size={24} />,
      title: "Live Chat",
      description: "Chat with our support team",
      contact: "Start Chat",
      availability: "Mon-Fri, 9AM-6PM",
      link: "https://www.vela.business/"
    },
    {
      icon: <Phone size={24} />,
      title: "Phone Support",
      description: "Speak with a support agent",
      contact: "+1 (555) 123-4567",
      availability: "Mon-Fri, 9AM-6PM",
    },
  ];

  const filteredFAQs =
    faqData[activeCategory as keyof typeof faqData]?.filter(
      (faq) =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  return (
    <div
      className={`min-h-screen flex overflow-x-hidden ${darkMode
          ? "bg-gradient-to-br from-[#181824] to-[#232336]"
          : "bg-gradient-to-br from-gray-50 to-blue-50"
        }`}
    >
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <main
        className={`flex-1 px-4 py-8 md:px-12 md:py-10 transition-all duration-200 ${collapsed
            ? "ml-20"
            : "ml-0 md:ml-52"
          }`}
      >
        {/* Top Bar */}
        <div className="ml-[100px] md:ml-0 mb-4">
          <NavBar />
        </div>

        {/* Page Title */}
        <div className="mb-6 mt-4 md:mt-12 flex flex-col items-center md:items-start ml-[60px] md:ml-0">

          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
            <div>
              <h2
                className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                  }`}
              >
                Help & Support
              </h2>
            </div>
          </div>
        </div>

        {/* Help & Support Layout (Responsive) */}
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 min-w-0">

          {/* ───────────────────── Left Sidebar ───────────────────── */}
          <div
            className="w-[calc(100%_-_74px)] ml-[74px]
md:w-80 md:ml-0 flex-shrink-0"
          >
            <div
              className={`rounded-2xl shadow-2xl border p-2 ${darkMode ? "bg-[#2c2c3a] border-white/10" : "bg-white border-gray-200"
                }`}
            >
              {/* Responsive Category Layout */}
              <div className="flex flex-wrap md:grid md:grid-cols-1 gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id as any)}
                    className={`flex-grow md:flex-grow-0 min-w-0 sm:min-w-[20%] md:min-w-0
              flex items-center p-1 md:p-1 rounded-xl transition-all duration-200 group
              ${activeCategory === category.id
                        ? darkMode
                          ? "bg-gradient-to-r from-pink-600/20 to-red-500/20 border border-pink-500/30"
                          : "bg-gradient-to-r from-pink-50 to-red-50 border border-pink-200"
                        : darkMode
                          ? "hover:bg-white/10"
                          : "hover:bg-gray-50"
                      }`}
                  >
                    <div
                      className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-xl
                transition-all duration-200 flex-shrink-0 ${activeCategory === category.id
                          ? "bg-gradient-to-r from-pink-600 to-red-500 text-white"
                          : darkMode
                            ? "bg-white/10 text-white/70 group-hover:text-white"
                            : "bg-gray-100 text-gray-600 group-hover:text-gray-900"
                        }`}
                    >
                      {category.icon}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-sm md:text-base font-medium truncate ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        {category.label}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ───────────────────── Right Content Area ───────────────────── */}
          <div className="flex-1 min-w-0">

            <div
              className="space-y-8 w-[calc(100%_-_74px)]
 ml-[74px] md:w-full md:ml-0"
            >

              {activeCategory === "contact" ? (
                /* ───────────── Contact Support Section ───────────── */
                <div className="space-y-6">

                  <div
                    className={`rounded-2xl shadow-2xl border p-6 sm:p-8 ${darkMode ? "bg-[#2c2c3a] border-white/10" : "bg-white border-gray-200"
                      }`}
                  >
                    <h3
                      className={`text-xl font-semibold mb-6 ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      Contact Our Support Team
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {contactMethods.map((method, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            if (method.link) {
                              window.open(method.link, "_blank");
                            }
                          }}
                          className={`p-6 rounded-xl border transition-all duration-200 hover:scale-[1.02] cursor-pointer ${darkMode
                              ? "bg-white/5 border-white/10 hover:bg-white/10"
                              : "bg-gray-50 border-gray-100 hover:bg-gray-100"
                            }`}
                        >
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-600 to-red-500 flex items-center justify-center text-white mb-4">
                            {method.icon}
                          </div>

                          <h4 className={`font-semibold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
                            {method.title}
                          </h4>

                          <p className={`text-sm mb-3 ${darkMode ? "text-white/60" : "text-gray-600"}`}>
                            {method.description}
                          </p>

                          <p className="font-medium text-pink-500 mb-2">{method.contact}</p>

                          <div className="flex items-center gap-2">
                            <Clock size={14} className={darkMode ? "text-white/40" : "text-gray-400"} />
                            <span className={`text-xs ${darkMode ? "text-white/40" : "text-gray-400"}`}>
                              {method.availability}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Additional Resources */}
                  <div
                    className={`rounded-2xl shadow-2xl border p-6 sm:p-8 ${darkMode ? "bg-[#2c2c3a] border-white/10" : "bg-white border-gray-200"
                      }`}
                  >
                    <h3 className={`text-xl font-semibold mb-4 ${darkMode ? "text-white" : "text-gray-900"}`}>
                      Additional Resources
                    </h3>

                    <div className="space-y-3">
                      {/* Your resource links… */}
                    </div>
                  </div>
                </div>
              ) : (
                /* ───────────── FAQ Section ───────────── */
                <div
                  className={`rounded-2xl shadow-2xl border p-6 sm:p-8 ${darkMode ? "bg-[#2c2c3a] border-white/10" : "bg-white border-gray-200"
                    }`}
                >
                  <h3 className={`text-xl font-semibold mb-6 ${darkMode ? "text-white" : "text-gray-900"}`}>
                    Frequently Asked Questions
                  </h3>

                  {/* Search Bar */}
                  <div className="mb-6">
                    <div className="relative max-w-full md:max-w-md">
                      <Search
                        className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? "text-white/40" : "text-gray-400"
                          }`}
                      />
                      <input
                        type="text"
                        placeholder="Search help articles..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-200 ${darkMode
                            ? "bg-white/5 border-white/10 text-white placeholder-white/40 focus:border-pink-500/50"
                            : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-pink-500"
                          }`}
                      />
                    </div>
                  </div>

                  {/* FAQ Items */}
                  {filteredFAQs.length === 0 ? (
                    <div className="text-center py-8">
                      <HelpCircle
                        size={48}
                        className={`mx-auto mb-4 ${darkMode ? "text-white/20" : "text-gray-300"}`}
                      />
                      <p className={`text-lg ${darkMode ? "text-white/60" : "text-gray-500"}`}>
                        No articles found for your search
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredFAQs.map((faq, index) => (
                        <details
                          key={index}
                          className={`group rounded-lg border transition-all duration-200 ${darkMode
                              ? "border-white/10 hover:border-white/20"
                              : "border-gray-200 hover:border-gray-300"
                            }`}
                        >
                          <summary
                            className={`flex items-center justify-between p-4 cursor-pointer ${darkMode ? "hover:bg-white/5" : "hover:bg-gray-50"
                              }`}
                          >
                            <span className={darkMode ? "text-white" : "text-gray-900"}>
                              {faq.question}
                            </span>
                            <ChevronRight
                              size={16}
                              className={`transition-transform duration-200 group-open:rotate-90 ${darkMode ? "text-white/40" : "text-gray-400"
                                }`}
                            />
                          </summary>

                          <div className={`px-4 pb-4 ${darkMode ? "text-white/80" : "text-gray-700"}`}>
                            {faq.answer}
                          </div>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>

        </div>

      </main>
    </div>
  );
};

export default HelpSupport; 